import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Sparkles, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";

const TRIAL_DAYS = 14;
const MONTHLY = 29;
const YEARLY = 290;

const FEATURES = [
  { feature: "Temperature & fridge logs (unlimited)", monthly: true, yearly: true, excel: false },
  { feature: "Customisable H&S audit templates", monthly: true, yearly: true, excel: false },
  { feature: "PDF exports for inspectors", monthly: true, yearly: true, excel: "partial" },
  { feature: "Event planning with kit checklists", monthly: true, yearly: true, excel: false },
  { feature: "Invoice generation + Stripe payment links", monthly: true, yearly: true, excel: false },
  { feature: "Batch & expiry tracking with trace-an-ingredient", monthly: true, yearly: true, excel: false },
  { feature: "Staff & training records (role-gated)", monthly: true, yearly: true, excel: false },
  { feature: "Multi-user team workspace, unlimited seats", monthly: true, yearly: true, excel: false },
  { feature: "Daily email digest at 8am UTC", monthly: true, yearly: true, excel: false },
  { feature: "Trend alerts (consecutive failed checks)", monthly: true, yearly: true, excel: false },
  { feature: "GDPR data export + 30-day soft delete", monthly: true, yearly: true, excel: false },
  { feature: "Refer another business → 1 free month each", monthly: true, yearly: true, excel: false },
  { feature: "Two months free (17% saving)", monthly: false, yearly: true, excel: false },
  { feature: "Priority email support", monthly: false, yearly: true, excel: false },
];

const FAQ = [
  { q: "Can I switch between monthly and yearly later?", a: "Yes — head to Settings → Billing → Manage in Stripe and switch up or down whenever you like. Pro-rata is handled by Stripe automatically." },
  { q: "Does the £29 include VAT?", a: "Prices are shown net of VAT. VAT is added at checkout where applicable per your billing address." },
  { q: "What if I have multiple sites?", a: "Each site gets its own workspace + its own subscription. Talk to us if you have 5+ sites — we'll work out a single-bill discount." },
  { q: "Do you offer a refund?", a: "If something goes wrong in your first 30 days, email us at info@obnenterprise.co.uk and we'll refund the month, no quibbles." },
  { q: "What payment methods do you take?", a: "All major cards, Apple Pay and Google Pay via Stripe. BACS / invoice billing on the yearly plan for businesses that need it — just ask." },
];

function FeatureCell({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-[#4e7b5b] mx-auto" strokeWidth={2.2} />;
  if (value === "partial") return <span className="text-xs text-[#7a3d12]">partial</span>;
  return <X className="w-4 h-4 text-[#c5beb0] mx-auto" strokeWidth={2} />;
}

export default function Pricing() {
  const [stats, setStats] = useState(null);
  useSEO({
    title: "Pricing — Hearth & Crate · £29/mo, 14-day free trial",
    description: "One simple price: £29/month or £290/year (save 2 months). 14-day free trial, no card required. Full FSA compliance suite for UK food businesses. Cancel anytime.",
    canonical: "https://hearthpro.co.uk/pricing",
  });
  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1c1917]" data-testid="pricing-page">
      <header className="border-b border-[#e5e2d9] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-medium tracking-tight">
            Hearth<span className="text-[#c0612f]">.</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-xs uppercase tracking-[0.22em] text-[#57534e]">
            <Link to="/" className="hover:text-[#1b3b36]">Home</Link>
            <Link to="/pricing" className="text-[#1b3b36]">Pricing</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="pricing-signin" className="text-sm text-[#1b3b36] hover:underline underline-offset-4">Sign in</Link>
            <Link to="/signup" data-testid="pricing-signup" className="text-xs uppercase tracking-[0.18em] bg-[#1b3b36] hover:bg-[#2c5a53] text-white px-4 py-2.5 rounded-[3px]">
              Start free trial
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-[#e5e2d9]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16 lg:py-24 text-center">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f]">Pricing</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1b3b36] mt-4 leading-[0.95]">
            One simple price.<br />
            <span className="italic text-[#4e7b5b]">No nasty surprises.</span>
          </h1>
          <p className="mt-6 text-base text-[#57534e] leading-relaxed">
            Unlimited team members. Unlimited records. Cancel any time. {TRIAL_DAYS}-day free trial on every plan.
          </p>

          {stats && stats.active_workspaces >= 3 && (
            <div
              data-testid="pricing-social-proof"
              className="mt-8 inline-flex items-center gap-2.5 bg-white border border-[#e5e2d9] rounded-full px-4 py-2 text-sm text-[#57534e] shadow-sm"
            >
              <Users className="w-3.5 h-3.5 text-[#4e7b5b]" strokeWidth={2} />
              <span>
                <b className="text-[#1b3b36]" data-testid="social-proof-active">{stats.active_workspaces}</b> UK food business{stats.active_workspaces === 1 ? "" : "es"} using Hearth
                {stats.joined_this_week > 0 && (
                  <>
                    <span className="mx-2 text-[#c8c2b6]">·</span>
                    <b className="text-[#c0612f]" data-testid="social-proof-week">{stats.joined_this_week}</b> joined this week
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="border-b border-[#e5e2d9]">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-16">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e5e2d9] rounded-[4px] p-8" data-testid="pricing-card-monthly">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Monthly</div>
              <div className="flex items-baseline gap-1.5 mt-4">
                <span className="font-display text-6xl text-[#1b3b36]">£{MONTHLY}</span>
                <span className="text-sm text-[#8a8476]">/month</span>
              </div>
              <p className="text-sm text-[#57534e] mt-3">Pay-as-you-go. Cancel anytime.</p>
              <Link to="/signup?plan=monthly" data-testid="pricing-cta-monthly" className="block mt-7 h-12 bg-[#1b3b36] hover:bg-[#2c5a53] text-white text-center leading-[3rem] rounded-[3px] tracking-wide transition-colors">
                Start {TRIAL_DAYS}-day trial
              </Link>
              <ul className="mt-7 space-y-3 text-sm text-[#1c1917]">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> All 8 modules, unlimited records</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> Unlimited team members</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> Daily email digest + trend alerts</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> Email support within 24 hours</li>
              </ul>
            </div>

            <div className="relative bg-white border-2 border-[#1b3b36] rounded-[4px] p-8 ring-4 ring-[#1b3b36]/5" data-testid="pricing-card-yearly">
              <span className="absolute -top-3 right-5 text-[10px] uppercase tracking-[0.22em] bg-[#c0612f] text-white px-3 py-1 rounded-[3px]">Best value · 17% off</span>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">Yearly</div>
              <div className="flex items-baseline gap-1.5 mt-4">
                <span className="font-display text-6xl text-[#1b3b36]">£{YEARLY}</span>
                <span className="text-sm text-[#8a8476]">/year</span>
              </div>
              <p className="text-sm text-[#57534e] mt-3">Two months free — £{(YEARLY / 12).toFixed(2)}/month effective.</p>
              <Link to="/signup?plan=yearly" data-testid="pricing-cta-yearly" className="block mt-7 h-12 bg-[#4e7b5b] hover:bg-[#3b6249] text-white text-center leading-[3rem] rounded-[3px] tracking-wide transition-colors">
                Start {TRIAL_DAYS}-day trial
              </Link>
              <ul className="mt-7 space-y-3 text-sm text-[#1c1917]">
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> Everything in Monthly</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> Two months free (£58 saved)</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> Priority email support</li>
                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-[#4e7b5b] shrink-0" strokeWidth={2} /> Lock in this price for 12 months</li>
              </ul>
            </div>
          </div>

          <div className="text-center text-xs text-[#8a8476] mt-8">
            <Sparkles className="w-3.5 h-3.5 inline -mt-0.5 mr-1 text-[#c0612f]" /> Refer another food business — you both get a free month. No cap.
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="border-b border-[#e5e2d9] bg-[#efece6]/40">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 py-16">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f]">Compare</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1b3b36] mt-3">Everything you get.</h2>

          <div className="mt-8 bg-white border border-[#e5e2d9] rounded-[4px] overflow-x-auto" data-testid="pricing-feature-table">
            <table className="w-full text-sm">
              <thead className="bg-[#efece6] text-[10px] uppercase tracking-[0.22em] text-[#57534e]">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Feature</th>
                  <th className="text-center px-3 py-3 font-medium">Monthly</th>
                  <th className="text-center px-3 py-3 font-medium">Yearly</th>
                  <th className="text-center px-3 py-3 font-medium">Spreadsheets</th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((row) => (
                  <tr key={row.feature} className="border-t border-[#e5e2d9]">
                    <td className="px-5 py-3 text-[#1c1917]">{row.feature}</td>
                    <td className="text-center px-3 py-3"><FeatureCell value={row.monthly} /></td>
                    <td className="text-center px-3 py-3"><FeatureCell value={row.yearly} /></td>
                    <td className="text-center px-3 py-3"><FeatureCell value={row.excel} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[#e5e2d9]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#c0612f]">Billing FAQ</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1b3b36] mt-3">Money questions.</h2>
          <div className="mt-8">
            {FAQ.map((f) => (
              <details key={f.q} className="group border-b border-[#e5e2d9] py-5" data-testid="pricing-faq-item">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-display text-lg text-[#1b3b36]">{f.q}</span>
                  <span className="text-[#8a8476] group-open:rotate-45 transition-transform text-xl leading-none mt-0.5">+</span>
                </summary>
                <p className="text-sm text-[#57534e] leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1b3b36]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-20 text-center">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-white leading-tight">Try it for {TRIAL_DAYS} days, on us.</h2>
          <p className="text-sm text-white/70 mt-4">No card. Export your data on day 14 if it's not for you.</p>
          <Link to="/signup" data-testid="pricing-final-cta" className="inline-flex items-center gap-2 mt-8 bg-white hover:bg-[#e9c4a3] text-[#1b3b36] font-medium px-7 py-4 rounded-[3px] tracking-wide transition-colors">
            Start free trial
            <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#e5e2d9] bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 flex flex-wrap items-center justify-between gap-4 text-xs text-[#8a8476]">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#4e7b5b]" strokeWidth={1.8} />
            <span>UK-based. GDPR-compliant.</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/legal/terms" className="hover:text-[#1b3b36]">Terms</Link>
            <Link to="/legal/privacy" className="hover:text-[#1b3b36]">Privacy</Link>
            <Link to="/legal/cookies" className="hover:text-[#1b3b36]">Cookies</Link>
            <Link to="/" className="hover:text-[#1b3b36]">Home</Link>
            <Link to="/login" className="hover:text-[#1b3b36]">Sign in</Link>
          </div>
          <div>© {new Date().getFullYear()} OBN Enterprise Ltd.</div>
        </div>
      </footer>
    </div>
  );
}
